import { Routes } from '@angular/router'

import { groupGuard } from '@yoshu-lib/guards/group.guard'

import { environment } from '@aubertduval/env/environment'

import { authGuard } from './core/auth/auth.guard'
import { redirectIfAuthGuard } from './core/auth/redirect-if-auth.guard'

export const routes: Routes = [
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layouts/main/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: environment.features['module.free-mode'] ? 'search' : 'comparison'
      },
      {
        path: 'comparison',
        loadComponent: () =>
          import('./pages/comparison/comparison.component').then((m) => m.ComparisonComponent)
      },
      {
        // Transient progress screen while a launch is in flight, so the user
        // leaves the create form immediately instead of waiting on it through
        // ingestion. Bounces back to the form if reached with nothing running.
        path: 'comparison/launching',
        loadComponent: () =>
          import('./pages/comparison/comparison-launch-progress.component').then(
            (m) => m.ComparisonLaunchProgressComponent
          )
      },
      {
        path: 'comparisons',
        loadComponent: () =>
          import('./pages/comparisons/comparisons-list.component').then(
            (m) => m.ComparisonsListComponent
          )
      },
      {
        // Manual-first comparison workspace. Demo surface for the AED tenant:
        // the page renders frozen fixtures and calls no API. Deliberately not
        // in the side menu: it is reachable by URL only until the feature is
        // real. Declared before `comparisons/:id` so `assisted` is not read as
        // a comparison id.
        path: 'comparisons/assisted/:reference',
        loadComponent: () =>
          import('./pages/assisted-comparison/assisted-comparison.component').then(
            (m) => m.AssistedComparisonComponent
          )
      },
      {
        path: 'comparisons/:id',
        loadComponent: () =>
          import('./pages/comparisons/comparison-detail.component').then(
            (m) => m.ComparisonDetailComponent
          )
      },
      {
        path: 'documents',
        loadComponent: () =>
          import('./pages/documents/documents.component').then((m) => m.DocumentsComponent)
      },
      {
        path: 'documents/:id',
        loadComponent: () =>
          import('./pages/documents/document-review.component').then(
            (m) => m.DocumentReviewComponent
          )
      },
      ...(environment.features['module.aed.knowledge-admin']
        ? ([
            {
              path: 'knowledge',
              canActivate: [groupGuard('aed_admin')],
              loadComponent: () =>
                import('./pages/knowledge/knowledge-documents.component').then(
                  (m) => m.KnowledgeDocumentsComponent
                )
            }
          ] as Routes)
        : []),
      {
        // Viewable by any authenticated user (read-only); write actions are
        // gated on tenant-admin role inside the component.
        path: 'admin/checks',
        loadComponent: () =>
          import('./pages/admin/checks-admin.component').then((m) => m.ChecksAdminComponent)
      },
      ...(environment.features['module.free-mode']
        ? ([
            {
              path: 'search',
              loadComponent: () =>
                import('./pages/search/chat-list.component').then((m) => m.ChatListComponent)
            },
            {
              path: 'interactions/free',
              loadChildren: () =>
                import('@yoshu-lib/features/interaction-modes/free-mode/free-mode.routes').then(
                  (m) => m.default
                ),
              data: { workflow: 'rag' }
            }
          ] as Routes)
        : [])
    ]
  },
  {
    path: 'login',
    canActivate: [redirectIfAuthGuard],
    loadComponent: () => import('@yoshu-lib/ui/login').then((m) => m.LoginComponent)
  },
  {
    path: '**',
    redirectTo: ''
  }
]
